import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { EDIT_PAGE_KEY_MAP, useAutoSaveParams } from '@/pages/strategic/hooks/useAutoSaveParams';
import { Project } from '../types';
import { MetricTextInfo } from '../utils/metricUtils';

/**
 * Custom hook for Weekly Project Highlight auto-save functionality
 * 
 * This hook manages:
 * - Auto-save detection and restoration for Weekly Project Highlight
 * - Tracks projects, pendingProjectDeletes, pendingContentDeletes, metricTextInfoMap, and batchedMetricsDataMap changes
 * 
 */
export const useWeeklyProjectHighlightAutoSave = (
  isEditing: boolean,
  projects: Project[],
  pendingProjectDeletes: Project[],
  pendingContentDeletes: Array<{id: string, projectId: string}>,
  metricTextInfoMap: Record<string, MetricTextInfo[]>,
  batchedMetricsDataMap: Record<string, { card_list: any[], trend_list: any[] }>,
  setProjects: (data: Project[]) => void,
  setPendingProjectDeletes: (data: Project[]) => void,
  setPendingContentDeletes: (data: Array<{id: string, projectId: string}>) => void,
  setOriginalProjects: (data: Project[]) => void,
  setMetricTextInfoMap: (data: Record<string, MetricTextInfo[]>) => void,
  setBatchedMetricsDataMap: (data: Record<string, { card_list: any[], trend_list: any[] }>) => void
) => {
  // Auto-save state
  const [autoSaveVisible, setAutoSaveVisible] = useState(false);

  // Auto-save: construct unique pageKey for Weekly Project Highlight
  const autoSavePageKey = useMemo(() => {
    return EDIT_PAGE_KEY_MAP.business_weekly_highlight;
  }, []);

  // Auto-save: get methods (check if auto-save exists)
  const autoSaveMethods: any = useAutoSaveParams({
    pageKey: autoSavePageKey,
  });

  const {
    getSaveParamsValue,
    showRestorePrompt,
    deleteSaveParamsByPageKey
  } = autoSaveMethods;

  // Initial params ref for auto-save (stores originalProjects - the baseline for comparison)
  // Structure must match saveParamsCur for proper comparison in useAutoSaveParams
  const initialParamsRef = useRef<{
    projects: Project[];
    originalProjects: Project[];
    pendingProjectDeletes: Project[];
    pendingContentDeletes: Array<{id: string, projectId: string}>;
    metricTextInfoMap: Record<string, MetricTextInfo[]>;
    batchedMetricsDataMap: Record<string, { card_list: any[], trend_list: any[] }>;
  }>({
    projects: [],
    originalProjects: [],
    pendingProjectDeletes: [],
    pendingContentDeletes: [],
    metricTextInfoMap: {},
    batchedMetricsDataMap: {},
  });

  // Current save params for auto-save comparison
  const currentSaveParams = useMemo(() => {
    if (!isEditing) {
      // When not editing, return empty params to prevent auto-save from triggering
      return {
        projects: [],
        originalProjects: [],
        pendingProjectDeletes: [],
        pendingContentDeletes: [],
        metricTextInfoMap: {},
        batchedMetricsDataMap: {},
      };
    }

    // When editing, save both current projects AND originalProjects (baseline)
    // CRITICAL: Also save metricTextInfoMap and batchedMetricsDataMap for proper restore
    return {
      projects: projects,
      originalProjects: initialParamsRef.current.originalProjects, // Save the baseline
      pendingProjectDeletes: pendingProjectDeletes,
      pendingContentDeletes: pendingContentDeletes,
      metricTextInfoMap: metricTextInfoMap, // Save metric metadata for titles
      batchedMetricsDataMap: batchedMetricsDataMap, // Save metrics data (card_list + trend_list)
    };
  }, [isEditing, projects, pendingProjectDeletes, pendingContentDeletes, metricTextInfoMap, batchedMetricsDataMap]);

  // Initial params for useAutoSaveParams - must match saveParamsCur structure
  // Compute inline to ensure it always uses the latest ref values
  const getInitialParamsForAutoSave = () => {
    return {
      projects: initialParamsRef.current.originalProjects, // At start, projects = originalProjects
      originalProjects: initialParamsRef.current.originalProjects,
      pendingProjectDeletes: initialParamsRef.current.pendingProjectDeletes,
      pendingContentDeletes: initialParamsRef.current.pendingContentDeletes,
      metricTextInfoMap: initialParamsRef.current.metricTextInfoMap,
      batchedMetricsDataMap: initialParamsRef.current.batchedMetricsDataMap,
    };
  };

  // Start auto-saving when in edit mode
  useAutoSaveParams({
    pageKey: autoSavePageKey,
    initialParams: getInitialParamsForAutoSave(),
    saveParamsCur: currentSaveParams,
    isBegin: isEditing,
    isChangeValue: true,
  });

  // Reset initialParams when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      initialParamsRef.current = {
        projects: [],
        originalProjects: [],
        pendingProjectDeletes: [],
        pendingContentDeletes: [],
        metricTextInfoMap: {},
        batchedMetricsDataMap: {},
      };
    }
  }, [isEditing]);

  // Handler: Check for auto-save before entering edit mode
  const handleEdit = useCallback((originalEditHandler: () => void) => {
    if (showRestorePrompt(autoSavePageKey)) {
      setAutoSaveVisible(true);
    } else {
      originalEditHandler();
    }
  }, [showRestorePrompt, autoSavePageKey]);

  // Handler: Initialize auto-save when entering edit mode
  // clonedProjects is the originalProjects (baseline before any edits)
  const initializeAutoSave = useCallback((
    clonedProjects: Project[], 
    initialMetricTextInfoMap: Record<string, MetricTextInfo[]>,
    initialBatchedMetricsDataMap: Record<string, { card_list: any[], trend_list: any[] }>
  ) => {
    initialParamsRef.current = {
      projects: clonedProjects, // At start, projects = originalProjects (baseline)
      originalProjects: clonedProjects, // Store the baseline for comparison
      pendingProjectDeletes: [],
      pendingContentDeletes: [],
      metricTextInfoMap: initialMetricTextInfoMap, // Store initial metric metadata
      batchedMetricsDataMap: initialBatchedMetricsDataMap, // Store initial metrics data
    };
  }, []);

  // Handler: Discard auto-save
  const handleAutoSaveCancel = useCallback(() => {
    setAutoSaveVisible(false);
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  // Handler: Restore from auto-save
  const handleAutoSaveConfirm = useCallback(() => {
    const savedData = getSaveParamsValue(autoSavePageKey) || {};

    setAutoSaveVisible(false);

    // Restore projects (the edited state)
    if (savedData.projects && Array.isArray(savedData.projects)) {
      setProjects(savedData.projects);
    }

    // Restore originalProjects (the baseline for comparison)
    // This is critical - we need the baseline to detect changes
    if (savedData.originalProjects && Array.isArray(savedData.originalProjects)) {
      setOriginalProjects(JSON.parse(JSON.stringify(savedData.originalProjects)));
    } else if (savedData.projects && Array.isArray(savedData.projects)) {
      // Fallback: if originalProjects not saved, use projects as baseline
      // (This shouldn't happen, but provides safety)
      setOriginalProjects(JSON.parse(JSON.stringify(savedData.projects)));
    }

    // Restore pending project deletes
    if (savedData.pendingProjectDeletes && Array.isArray(savedData.pendingProjectDeletes)) {
      setPendingProjectDeletes(savedData.pendingProjectDeletes);
    }

    // Restore pending content deletes
    if (savedData.pendingContentDeletes && Array.isArray(savedData.pendingContentDeletes)) {
      setPendingContentDeletes(savedData.pendingContentDeletes);
    }

    // CRITICAL: Restore metricTextInfoMap for metric display names (WAU, DAU, etc.)
    if (savedData.metricTextInfoMap && typeof savedData.metricTextInfoMap === 'object') {
      setMetricTextInfoMap(savedData.metricTextInfoMap);
    }

    // CRITICAL: Restore batchedMetricsDataMap for metrics data (card_list + trend_list)
    // This is why metrics were stuck in "Loading..." - the trend data was missing!
    if (savedData.batchedMetricsDataMap && typeof savedData.batchedMetricsDataMap === 'object') {
      setBatchedMetricsDataMap(savedData.batchedMetricsDataMap);
    }

    // Initialize auto-save initial params with restored baseline
    // After restore, projects = restored projects, originalProjects = restored baseline
    const restoredOriginalProjects = savedData.originalProjects || savedData.projects || [];
    initialParamsRef.current = {
      projects: savedData.projects || restoredOriginalProjects, // Current edited state
      originalProjects: restoredOriginalProjects, // Baseline for comparison
      pendingProjectDeletes: savedData.pendingProjectDeletes || [],
      pendingContentDeletes: savedData.pendingContentDeletes || [],
      metricTextInfoMap: savedData.metricTextInfoMap || {},
      batchedMetricsDataMap: savedData.batchedMetricsDataMap || {},
    };

    // Clear the old auto-save data after restoring
    // This prevents the restore prompt from showing again when clicking Edit
    // Auto-save will start fresh with the restored data as the new baseline
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [
    getSaveParamsValue, 
    autoSavePageKey, 
    deleteSaveParamsByPageKey, 
    setProjects, 
    setOriginalProjects, 
    setPendingProjectDeletes, 
    setPendingContentDeletes,
    setMetricTextInfoMap,
    setBatchedMetricsDataMap
  ]);

  // Clear auto-save after successful save
  const clearAutoSave = useCallback(() => {
    deleteSaveParamsByPageKey(autoSavePageKey);
  }, [deleteSaveParamsByPageKey, autoSavePageKey]);

  return {
    // State
    autoSaveVisible,
    autoSavePageKey,

    // Handlers
    handleEdit,
    initializeAutoSave,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearAutoSave,
  };
};
